/* eslint-disable @typescript-eslint/no-floating-promises */
import { clearCache, useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';
import { DailyStatus } from 'services/api/models/SymptomTrackingDTO';
import { symptomTrackingActions } from 'store/symptomTracking';
import {
  selectDailyStatuses,
  selectIsPeriodReminderEnabled,
  selectReminderTime,
} from 'store/symptomTracking/selectors';
import useSchedulePeriodNotifications from './useSchedulePeriodNotifications';

export type CycleStats = {
  start: Dayjs;
  periodLength: number;
  totalLength: number;
  completed: boolean;
  predicted?: boolean;
};

export default function useSymptomTrackingData() {
  const today = dayjs().set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0);
  const dispatch = useDispatch();
  const { loading: loadingData, refresh } = useRequest(() => ApiSymptomTrackingManager.getHistory(), {
    cacheKey: 'tracking.history',
    onSuccess: data => {
      dispatch(symptomTrackingActions.setDailyStatuses(data));
    },
  });

  const statusesList = useSelector(selectDailyStatuses);

  const reminderTime = useSelector(selectReminderTime);
  const areDailyNotificationsEnabled = reminderTime !== null;
  const isPeriodReminderEnabled = useSelector(selectIsPeriodReminderEnabled);
  const { schedulePeriodReminder } = useSchedulePeriodNotifications();

  const { run: fillPeriodDays } = useRequest(
    (startDate: Dayjs, numberOfDays: number) => {
      const startDateMidnight = startDate.set('hour', 0).set('minute', 0).set('second', 0);
      const endDate = dayjs.min(
        startDateMidnight.add(numberOfDays - 1, 'days'),
        today.set('hour', 0).set('minute', 0).set('second', 0),
      ) as Dayjs;
      dispatch(
        symptomTrackingActions.addPeriodToEmptyDays(
          Array.from({ length: endDate.diff(startDateMidnight, 'days') + 1 }, (_, i) =>
            startDate.add(i, 'days').format('YYYY-MM-DD'),
          ),
        ),
      );
      return ApiSymptomTrackingManager.fillPeriodDays(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'));
    },
    {
      manual: true,
      onFinally: () => {
        clearCache('tracking.history');
        refresh();
      },
    },
  );

  const { run: removePeriodFromDay } = useRequest(
    (date: Dayjs) => {
      dispatch(symptomTrackingActions.removePeriodFromDay(date.format('YYYY-MM-DD')));
      return ApiSymptomTrackingManager.removePeriodFromDay(date.format('YYYY-MM-DD'));
    },
    {
      manual: true,
      onFinally: () => {
        clearCache('tracking.history');
        refresh();
      },
    },
  );

  const loading = statusesList === undefined && loadingData;
  const hasTrackingBeenUsed = !loading && statusesList && statusesList.length > 0;

  const statusPerDay = Object.fromEntries((statusesList || []).map(status => [status.date as string, status]));

  const getSpecificDailyStatus = (date: Dayjs) => {
    return statusPerDay[date.format('YYYY-MM-DD')];
  };

  const hasTrackingForToday = getSpecificDailyStatus(today) !== undefined;

  const getDisplayedDays = (selectedDate: Dayjs) => {
    if (today.format('YYYY-MM') === selectedDate.format('YYYY-MM')) {
      // show last 31 days if we are looking at current month
      // (to avoid having empty graphs at the start of the month)
      return Array.from({ length: 31 }, (_, i) => today.subtract(i, 'days')).reverse();
    }

    return Array.from({ length: selectedDate.daysInMonth() }, (_, i) =>
      dayjs(new Date(selectedDate.year(), selectedDate.month(), i + 1)),
    );
  };

  const getStatusValueForDisplayedDays = (
    value: keyof DailyStatus,
    displayedDays: Dayjs[],
    defaultValue: any = null,
  ): any[] => {
    return displayedDays.map(day => {
      const dailyStatus = getSpecificDailyStatus(dayjs(day));
      return dailyStatus ? dailyStatus[value] : defaultValue;
    });
  };

  const getPeriodStats = useCallback(() => {
    if (!statusesList) return [];

    let isInPeriod = false;
    let lastBleedingDay: string | null = null;
    const cyclesHistory: CycleStats[] = [];
    const MAX_GAP_DAYS = 3;

    statusesList.forEach(status => {
      if (status.menstruation_amount > 0) {
        const currentDate = dayjs(status.date);

        if (!isInPeriod) {
          // Starting a new period
          if (cyclesHistory.length > 0) {
            const previousCycle = cyclesHistory[cyclesHistory.length - 1];
            previousCycle.completed = true;
            previousCycle.totalLength = currentDate.diff(dayjs(previousCycle.start), 'days');
            // Update previous cycle's period length based on its last bleeding day
            if (lastBleedingDay) {
              previousCycle.periodLength = dayjs(lastBleedingDay).diff(dayjs(previousCycle.start), 'days') + 1;
            }
          }

          cyclesHistory.push({
            start: currentDate,
            periodLength: 0, // Will be calculated when cycle ends or when we have the last bleeding day
            totalLength: 0,
            completed: false,
          });
          isInPeriod = true;
        } else if (lastBleedingDay) {
          // Check if we're still within the same period (allowing up to 3 days gap)
          const daysSinceLastBleeding = currentDate.diff(dayjs(lastBleedingDay), 'days');

          if (daysSinceLastBleeding > MAX_GAP_DAYS + 1) {
            // Gap is too large, start a new cycle
            const previousCycle = cyclesHistory[cyclesHistory.length - 1];
            previousCycle.completed = true;
            previousCycle.totalLength = currentDate.diff(dayjs(previousCycle.start), 'days');
            // Update previous cycle's period length based on its last bleeding day
            previousCycle.periodLength = dayjs(lastBleedingDay).diff(dayjs(previousCycle.start), 'days') + 1;

            cyclesHistory.push({
              start: currentDate,
              periodLength: 0, // Will be calculated when cycle ends
              totalLength: 0,
              completed: false,
            });
          }
          // If continuing the current period, we don't need to do anything here
          // The period length will be calculated based on the last bleeding day
        }

        lastBleedingDay = status.date || null;
      } else if (isInPeriod && lastBleedingDay) {
        // Check if we should end the current period
        const daysSinceLastBleeding = dayjs(status.date).diff(dayjs(lastBleedingDay), 'days');
        if (daysSinceLastBleeding > MAX_GAP_DAYS) {
          isInPeriod = false;
        }
      }
    });

    // Handle the last cycle
    if (cyclesHistory.length > 0) {
      const lastCycle = cyclesHistory[cyclesHistory.length - 1];
      lastCycle.totalLength = dayjs(today).diff(dayjs(lastCycle.start), 'days') + 1;
      // Update last cycle's period length based on its last bleeding day
      if (lastBleedingDay) {
        lastCycle.periodLength = dayjs(lastBleedingDay).diff(dayjs(lastCycle.start), 'days') + 1;
      }
    }

    return cyclesHistory;
  }, [statusesList, today]);

  const cycleHistory = useMemo(() => getPeriodStats(), [getPeriodStats]);
  const completedCycles = useMemo(() => cycleHistory.filter(cycle => cycle.completed), [cycleHistory]);

  // average cycle length
  const cycleStats = useMemo(() => {
    const stats = {
      average: completedCycles.reduce((acc, cycle) => acc + cycle.totalLength, 0) / completedCycles.length,
      periodLengthAverage: completedCycles.reduce((acc, cycle) => acc + cycle.periodLength, 0) / completedCycles.length,
      maxVariationFromAverage: 0,
    };
    stats.maxVariationFromAverage = completedCycles.reduce(
      (acc, cycle) => Math.max(acc, Math.abs(stats.average - cycle.totalLength)),
      0,
    );

    return stats;
  }, [completedCycles]);

  const probableNextPeriodStartDates = useMemo(() => {
    if (cycleHistory.length < 2) return null;
    const lastCycle = cycleHistory[cycleHistory.length - 1];
    return {
      start: lastCycle.start.add(cycleStats.average - cycleStats.maxVariationFromAverage, 'days'),
      end: lastCycle.start.add(cycleStats.average + cycleStats.maxVariationFromAverage, 'days'),
    };
  }, [cycleHistory, cycleStats]);

  useEffect(() => {
    if (isPeriodReminderEnabled && probableNextPeriodStartDates) {
      // re-schedule period reminders in case the user adds a new period,
      // removes a period, updates a period or does anything that could affect the prediction
      schedulePeriodReminder(probableNextPeriodStartDates.start, areDailyNotificationsEnabled);
    }
  }, [isPeriodReminderEnabled, probableNextPeriodStartDates, areDailyNotificationsEnabled]);

  const daysUntilNextPeriod = useMemo(() => {
    if (!probableNextPeriodStartDates) return null;
    const min = probableNextPeriodStartDates.start.diff(today, 'days');
    const max = probableNextPeriodStartDates.end.diff(today, 'days');
    return { min, max };
  }, [probableNextPeriodStartDates, today]);

  const probableNextPeriodDates = useMemo(() => {
    if (!probableNextPeriodStartDates) return null;
    return {
      start: probableNextPeriodStartDates.start,
      end: probableNextPeriodStartDates.end.add(cycleStats.periodLengthAverage, 'days'),
    };
  }, [probableNextPeriodStartDates, cycleStats]);

  const isWithinPeriod = useMemo(() => {
    const lastCycle = cycleHistory[cycleHistory.length - 1];
    if (!lastCycle) return false;

    return today.isSameOrBefore(lastCycle.start.add(lastCycle.periodLength, 'days'), 'day');
  }, [cycleHistory, today]);

  const probableDaysUntilEndOfPeriod = useMemo(() => {
    if (!isWithinPeriod) return null;
    if (!cycleStats.periodLengthAverage) return null;
    const lastCycle = cycleHistory[cycleHistory.length - 1];
    if (!lastCycle) return null;
    return Math.max(0, lastCycle.start.add(cycleStats.periodLengthAverage, 'days').diff(today, 'day'));
  }, [cycleHistory, today, cycleStats.periodLengthAverage, isWithinPeriod]);

  const cycleDaysWithCenteredPeriod = useMemo(() => {
    const midpoints: Dayjs[] = [];

    // the first midpoint is the start of the first cycle
    if (cycleHistory.length > 0) midpoints.push(cycleHistory[0].start);

    // for all cycles except the last one, add the midpoint of the cycle to the list
    for (let i = 0; i < cycleHistory.length - 1; i += 1) {
      // for all cycles except the last one, add the midpoint of the cycle to the list
      const cycle = cycleHistory[i];
      const midpoint = dayjs(cycle.start).add(cycle.totalLength / 2, 'days');
      midpoints.push(midpoint);
    }

    // for the last cycle, it's a bit more complicated
    if (cycleHistory.length > 0) {
      const lastCycle = cycleHistory[cycleHistory.length - 1];
      const lastCycleDay = lastCycle.start.add(lastCycle.totalLength, 'days');
      const predictedMidpoint = probableNextPeriodStartDates
        ? lastCycle.start.add(probableNextPeriodStartDates.start.diff(lastCycle.start, 'days') / 2, 'days')
        : null;
      if (predictedMidpoint === null || lastCycleDay.isSameOrBefore(predictedMidpoint, 'day')) {
        // no prediction or before the prediction: add the last day of the current cycle
        midpoints.push(lastCycleDay);
      } else if (probableNextPeriodStartDates) {
        // we have a prediction and the last day of the current cycle is after the prediction:
        // then we create a new "predicted" cycle
        midpoints.push(
          lastCycle.start.add(probableNextPeriodStartDates.start.diff(lastCycle.start, 'days') / 2, 'days'),
        );
        midpoints.push(dayjs.max(probableNextPeriodStartDates.start, lastCycleDay));
      }
    }

    const daysPerCycle = [];

    for (let m = 0; m < midpoints.length - 1; m += 1) {
      const days = Array.from({ length: midpoints[m + 1].diff(midpoints[m], 'days') }, (_, j) =>
        midpoints[m].add(j, 'days'),
      );
      if (m < cycleHistory.length) {
        daysPerCycle.push({ days, cycle: cycleHistory[m] });
      } else {
        daysPerCycle.push({
          days,
          cycle: {
            start: probableNextPeriodStartDates?.start,
            periodLength: cycleStats.periodLengthAverage,
            totalLength: cycleStats.average,
            completed: false,
            predicted: true,
          },
        });
      }
    }
    return daysPerCycle;
  }, [cycleHistory, cycleStats, probableNextPeriodStartDates]);

  const doesStatusHaveDataOtherThanPeriod = (status: DailyStatus) => {
    return (
      status.general_state !== null ||
      status.pain_level !== null ||
      status.symptoms.length > 0 ||
      status.extra_symptoms ||
      status.comment
    );
  };

  const dataOtherThanPeriod = useMemo(() => {
    const daysWithData = statusesList?.filter(doesStatusHaveDataOtherThanPeriod);
    const firstDayWithData = daysWithData?.[0];
    return {
      firstDayWithData,
      daysWithData,
    };
  }, [statusesList]);

  const hasDataOtherThanPeriod = useMemo(() => {
    return dataOtherThanPeriod.firstDayWithData !== undefined;
  }, [dataOtherThanPeriod]);

  return {
    getSpecificDailyStatus,
    getDisplayedDays,
    getStatusValueForDisplayedDays,
    fillPeriodDays,
    removePeriodFromDay,
    refresh,
    hasTrackingBeenUsed,
    hasTrackingForToday,
    loading,
    cycleHistory,
    cycleStats,
    cycleDaysWithCenteredPeriod,
    probableNextPeriodStartDates,
    probableNextPeriodDates,
    daysUntilNextPeriod,
    isWithinPeriod,
    probableDaysUntilEndOfPeriod,
    dataOtherThanPeriod,
    hasDataOtherThanPeriod,
  };
}
